import React, { useContext } from 'react'
import styled, { ThemeContext } from 'styled-components'
import Modal from '@jarvis.oddle/core/components/Modal'
import { H2, Text } from '@jarvis.oddle/core/components/Typo'
import { TranslationContext } from '@jarvis.oddle/core/components/Translation'
import modalController, {
  makeControlledModal
} from 'src/decorators/ModalController'
import { paddingSizes } from '@jarvis.oddle/core/theme/default'
import breakpoint from 'src/utils/styledComponentsBreakpoint'
import IconClose from '@jarvis.oddle/core/components/Icons/IconClose'
import Flex from '@jarvis.oddle/core/components/Flex'
import Div from '@jarvis.oddle/core/components/Div'
import IconEmployee from 'src/components/icons/IconEmployee'
import IconArrow from '@jarvis.oddle/core/components/Icons/IconArrow'
import Divider from '@jarvis.oddle/core/components/Divider'
import IconOddleLogistic from 'src/components/icons/IconOddleLogistic'

const Container = styled(Modal.FlexContainer).attrs(() => ({
  width: '100%',
  height: '100%',
  flexDirection: 'column',
  padding: paddingSizes.medium
}))`
  position: relative;
  background: ${(props) => props.theme.colorContainerColor};
  width: 95%;
  box-shadow: 0 0 8px rgba(0, 0, 0, 0.2);
  border-radius: 5px;
  transform: translateX(-50%) translateY(-50%);
  top: 50%;
  left: 50%;
  height: fit-content;
  ${breakpoint('desktop')`
    width: 100%;
    max-width: 600px;
    height: 100%;
    max-height: 492px;
  `}
`

const CloseButtonContainer = styled(Flex).attrs(() => ({
  flexDirection: 'row',
  justifyContent: 'center',
  alignItems: 'center',
  role: 'button'
}))`
  position: absolute;
  top: 10px;
  right: 10px;
  width: 40px;
  height: 40px;
  cursor: pointer;
  z-index: 10;
`

const StyledIconEmployee = styled(Div)`
  position: relative;
  margin-top: 40px;
  width: 76%;
  height: 160px;
  border-radius: 4px;
  border: 1px solid ${(props) => props.theme.colorLinesAndBorders};
  display: flex;
  justify-content: center;
`

const StyledIconBackground = styled(Div)`
  position: absolute;
  top: 40%;
  width: 100%;
  bottom: 29.95%;
  height: 64.15px;
  background: linear-gradient(180deg, rgba(243, 246, 255, 0) 0%, #f3f6ff 100%);
  z-index: -10;
  ${breakpoint('desktop')`
    top: 35%;
  `}
`

const StyledListOfHelp = styled.ul`
  width: 100%;
  list-style-type: none;
  display: flex;
  flex-direction: column;
  padding: 0;
  margin: 0;
`

const StyledHelp = styled.li`
  margin-bottom: 10px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  cursor: pointer;
`

const StyledContent = styled(Flex)`
  margin-top: 10px;
  flex-direction: column;
`
const StyledDivider = styled(Divider)`
  margin-bottom: 10px;
`
const StyledOddleLogistic = styled(Div)`
  display: flex;
  justify-content: center;
  margin-top: 30px;
`
const StyledNeedHelp = styled(H2)`
  color: ${props => props.theme.colorHeadingsFont};
`

const StyledHelpItem = styled(Text)`
  color: ${props => props.theme.colorHeadingsFont};
`

interface NeedHelpModalProps {
  modal: {
    show: boolean
    onClose: (x: () => void) => void
  }
  isPickup: boolean
  phoneStore: string
  emailStore: string
  addressStore: string
  id: string
  deliveryFulfilmentParty: {
    deliveryParty?: string
    partnerDisplayName?: string
  }
  enableChatSupportByCountry: boolean
}

const NeedHelpModal = ({
  modal,
  isPickup,
  phoneStore,
  emailStore,
  addressStore,
  id,
  deliveryFulfilmentParty,
  enableChatSupportByCountry
}: NeedHelpModalProps): JSX.Element => {
  const { t } = useContext(TranslationContext)
  const theme = useContext(ThemeContext)
  return (
    <Modal {...modal} show={modal.show} slideInAnimation='right'>
      <Container>
        <CloseButtonContainer onClick={modal.onClose}>
          <IconClose width={14} height={14} />
        </CloseButtonContainer>
        <Flex justifyContent='center'>
          <StyledIconEmployee>
            <IconEmployee />
            <StyledIconBackground />
          </StyledIconEmployee>
        </Flex>
        <StyledContent>
          <StyledNeedHelp>{t('I need help with...')}</StyledNeedHelp>
          <StyledListOfHelp>
            <StyledHelp
              data-testid={isPickup ? 'helpPickupButton' : 'helpDeliveryButton'}
              onClick={() => {
                if (
                  !enableChatSupportByCountry ||
                  isPickup ||
                  deliveryFulfilmentParty.deliveryParty === 'The restaurant'
                ) {
                  modal.onClose(() =>
                    modalController.showModal('ContactRestaurantModal', {
                      id,
                      deliveryFulfilmentParty,
                      isPickup,
                      phoneStore,
                      emailStore,
                      addressStore,
                      enableChatSupportByCountry
                    })
                  )
                  return
                }

                modal.onClose(() =>
                  modalController.showModal('ContactLogisticModal', {
                    id,
                    helpOption: 'delivery',
                    deliveryFulfilmentParty,
                    isPickup,
                    phoneStore,
                    emailStore,
                    addressStore,
                    enableChatSupportByCountry
                  })
                )
              }}
            >
              <StyledHelpItem bold>{isPickup ? t('Pickup') : t('Delivery')}</StyledHelpItem>
              <IconArrow direction='right' fill={theme.colorHeadingsFont} />
            </StyledHelp>
            <StyledDivider />
            <StyledHelp
              data-testid='helpItemsInOrderButton'
              onClick={() =>
                modal.onClose(() =>
                  modalController.showModal('ContactRestaurantModal', {
                    id,
                    deliveryFulfilmentParty,
                    isPickup,
                    phoneStore,
                    emailStore,
                    addressStore,
                    enableChatSupportByCountry
                  })
                )
              }
            >
              <StyledHelpItem bold>{t('Items in my order')}</StyledHelpItem>
              <IconArrow direction='right' fill={theme.colorHeadingsFont}/>
            </StyledHelp>
            <StyledDivider />
            <StyledHelp
              data-testid='helpPaymentButton'
              onClick={() =>
                modal.onClose(() =>
                  modalController.showModal('ContactRestaurantModal', {
                    id,
                    deliveryFulfilmentParty,
                    isPickup,
                    phoneStore,
                    emailStore,
                    addressStore,
                    enableChatSupportByCountry
                  })
                )
              }
            >
              <StyledHelpItem bold>{t('Payment')}</StyledHelpItem>
              <IconArrow direction='right' fill={theme.colorHeadingsFont} />
            </StyledHelp>
          </StyledListOfHelp>
        </StyledContent>
        <StyledOddleLogistic>
          <IconOddleLogistic width={86} height={13} />
        </StyledOddleLogistic>
      </Container>
    </Modal>
  )
}

export default makeControlledModal(NeedHelpModal, 'NeedHelpModal')
